import * as React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { withStyles } from '../../settings/styles';
import { Edit, Create } from '@mui/icons-material';
import { BroadcastType } from '../../model/overlay/types';
import { IServiceState } from '../../model/services/types';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { NavigationTabProps } from '../../model/navigation/types';
import { loadingOverlay, broadcastMessage } from '../../model/overlay/actions';
import { AppState } from '../../model/reducers';
import { UserState } from '../../model/user/types';
import { validate as uuidValidate } from 'uuid';
import _ from "lodash";
import ObjectSelector from '../inputs/ObjectSelector';
import DeleteButton from '../buttons/DeleteButton';
import EditButton from '../buttons/EditButton';
import CancelButton from '../buttons/CancelButton';
import CreateButton from '../buttons/CreateButton';
import TextProperty from '../properties/TextProperty';
import { PropertyType } from '../../types/PropertyType';
import { vsprintf } from 'sprintf-js';

const MAX_WIDTH = 420;

// -----------------------------------------------  //
// ---------------- State & Props ----------------  //
// -----------------------------------------------  //

interface State 
{
    open: boolean;
    object: IObjectState;
};

interface LocalProps extends WithTranslation
{
    open: boolean;
    object: IObject;
    editing?: boolean;
    zIndex?: number;
    onClose: ()=>void;
    onCreate?: (json: {})=>void;
    onEdit?: (json: {})=>void;
    onDelete?: (id: string)=>void;
}

interface StateProps
{
    user: UserState;
    services: IServiceState[];
}

interface DispatchProps
{
    loading: ()=>void;
    doneLoading: ()=>void;
    broadcast: (msg: string, type?: BroadcastType) => void;
}

type Props = LocalProps & StateProps & DispatchProps;

// -----------------------------------------------  //
// -------------------- Class --------------------  //
// -----------------------------------------------  //

class CreateEditDialog extends React.Component<Props, State>
{
    validators: {[key: string]: (newValue: unknown, property: IPropertyState) => string}; // MOVE

    constructor(props: any)
    {
        super(props);
        const { t } = this.props;

        // Init State
        this.state = {
            open: false,
            object: this.convertToObjectState(this.props.object)
        };

        this.validators = { // MOVE
            duplicate: (newValue: unknown, property: IPropertyState) => (
                (property.unique && property.valueList.find(val=>val===newValue)) ? 
                    `${t('dialogs.create.errors.alreadyAdded')}` : ""
            ),
            uuid: (newValue: unknown, property: IPropertyState) => (
                !uuidValidate(newValue as string) ? `${t('dialogs.create.errors.invalidFormat')}` : ""
            ),
            name: (newValue: unknown, property: IPropertyState) => (
                (newValue as string).length < 4 ? vsprintf(t('dialogs.create.errors.nameTooShort'), [4]) :
                (newValue as string).length > 20 ? vsprintf(t('dialogs.create.errors.nameTooLong'), [20]) : ""
            ),
            password: (newValue: unknown, property: IPropertyState) => (
                (newValue as string).length < 4 ? vsprintf(t('dialogs.create.errors.passwordTooShort'), [4]) :
                (newValue as string).length > 50 ? vsprintf(t('dialogs.create.errors.passwordTooLong'), [30]) : ""
            ),
            emptyArray: (newValue: unknown, property: IPropertyState) => (
                property.valueList.length <= 0 ? t('dialogs.create.errors.noAdded') : ""
            ),
        }

        // Bind Renders
        this.checkValidation = this.checkValidation.bind(this); // MOVE
        this.actionCreate = this.actionCreate.bind(this);
        this.actionEdit = this.actionEdit.bind(this);
        this.actionCancel = this.actionCancel.bind(this);
        this.actionDelete = this.actionDelete.bind(this);
    }

    componentDidUpdate(prevProps: Props) 
    {
        if (!_.isEqual(prevProps.object, this.props.object)) 
        {
            this.setState({
                object: this.convertToObjectState(this.props.object)
            });
        }
    }

    // ============================================ //
    //               Render Functions               //
    // ============================================ //

    render()
    {
        let properties = _.cloneDeep(this.state.object.properties)
        const onChange = () => {
            this.setState({object:{
                ...this.state.object,
                properties
            }})
        }

        return (<>
            <Dialog onClose={this.props.onClose} aria-labelledby="" open={this.props.open} >
                <Box sx={{maxWidth: MAX_WIDTH}}>
                    <DialogTitle id="create-edit-dialog-title" sx={{paddingBottom: "0px"}}>
                        {this.renderTitle()}
                    </DialogTitle>
                    <DialogContent sx={{marginBottom: "10px", marginTop: "0px"}}>
                        {this.renderObjectProperties(properties, onChange)}
                    </DialogContent>
                    <DialogActions>
                        {this.renderActions()}
                    </DialogActions>
                </Box>
            </Dialog>
        </>);
    }

    renderTitle()
    {
        const { t } = this.props;
        const Icon = this.state.object.icon ?? this.props.editing ? Edit : Create;
        return (<>
            <Typography>
                <Icon sx={{verticalAlign: "middle", ml: 1, mr: 2}} />
                {`${this.props.editing ? t('dialogs.edit.title') : t('dialogs.create.title')} ${this.state.object.title}`}
            </Typography>
        </>);
    }

    renderObjectSelection(objectsState: IObjectsState, onChange: ()=>void)
    {
        const title = `${this.props.t('dialogs.create.choose')} ${objectsState.title}`;
        const objects = objectsState.objects;
        const disabled = this.props.editing;

        return (<ObjectSelector 
            sx={{ml: 1, mr: 1}}
            title={title} 
            objects={objects} 
            selectedObject={objectsState.selectHandle}
            onSelection={(objectHandle: number)=>{
                objectsState.selectHandle = objectHandle;
                onChange();
            }}
            disabled={disabled}
        />);
    }

    renderObjectProperties(
        properties: (IPropertyState | IObjectsState)[], 
        onChange: ()=>void ) : JSX.Element
    {
        return (<>
            {
                properties.map((item: IPropertyState | IObjectsState, index: number) => {
                    if(this.isObjectsState(item))
                    {
                        return <Box key={`create-edit-dialog-property-${item.id}`}>
                            {this.renderObjectSelection(item, onChange)} 
                            {this.renderObjectProperties(item.objects[item.selectHandle].properties, onChange)}
                        </Box>
                    }
                    return <Box key={`create-edit-dialog-property-${item.id}`}>
                        {this.renderObjectInputFields(item, onChange)}
                    </Box>
                })
            }
        </>);
    }

    renderObjectInputFields(property: IPropertyState, onPropertyChange: ()=>void)
    {
        let onChange = (value: string) => {
            property.value = value;
            property.errors = property.array ? [] : this.checkValidation(value, property);
            onPropertyChange();
        }

        let onAdd = property.array ? (value: string) => {
            property.errors = this.checkValidation(value, property);
            property.value = property.errors.length > 0 ? value : "";
            property.valueList = property.errors.length > 0 ? property.valueList : [...property.valueList, value];
            onPropertyChange();
        } : undefined;

        let onDelete = (index: number) => {
            property.valueList.splice(index, 1);
            onPropertyChange();
        }

        let propertyInput = () => {
            switch(property.type)
            {
                case PropertyType.NAME:
                case PropertyType.UUID:
                case PropertyType.LINK:
                case PropertyType.PASSWORD:
                case PropertyType.TEXT:
                    return (<>
                        <TextProperty 
                            property={property}
                            onChange={onChange}
                            onAdd={onAdd}
                            onDelete={onDelete} 
                        />
                    </>);
                default:
                    return <></>
            }
        }

        return (<Box key={`property-input-${property.id}`} sx={{mt: "10px", mb: "10px"}}>
            {propertyInput()}
        </Box>);
    }

    renderActions()
    {
        return this.props.editing ? (<>
            <CancelButton onClick={this.actionCancel} />
                {
                    this.props.onDelete ? 
                        <DeleteButton onClick={this.actionDelete} /> : 
                        <></>
                }
            <EditButton   onClick={this.actionEdit} />
        </>) : (<>
            <CancelButton onClick={this.actionCancel} />
            <CreateButton onClick={this.actionCreate} />
        </>);
    }

    // ============================================ //
    //              Action Functions                //
    // ============================================ //

    actionCreate()
    {
        if(this.props.onCreate && this.createEditValidation())
        {
            this.props.onCreate(this.convertToJson(this.state.object));
        }
    }

    actionEdit()
    {
        if(this.props.onEdit && this.createEditValidation())
        {
            this.props.onEdit(this.convertToJson(this.state.object));
        }
    }

    actionCancel()
    {
        this.props.onClose();
    }
    
    actionDelete()
    {
        if(this.props.onDelete)
        {
            this.props.onDelete(this.state.object.id);
        }
    }
    
    // ============================================ //
    //               Check Functions                //
    // ============================================ //

    isObjectsState(objects: IPropertyState | IObjectsState): objects is IObjectsState 
    {
        return (objects as IObjectsState).objects !== undefined;
    }

    checkValidation(newValue: unknown, oldProperty: IPropertyState, done: boolean = false) // MOVE
    {
        let validators: ((newValue: unknown, oldProperty: IPropertyState)=>string)[] = [];
        if(oldProperty.array)
        {
            if(oldProperty.unique)
            {
                validators.push(this.validators.duplicate);
            }
            if(done)
            {
                validators.push(this.validators.emptyArray);
            }
        }

        if(oldProperty.array === undefined ? done : !done)
        {
            switch(oldProperty.type)
            {
                case PropertyType.UUID:
                    validators.push(this.validators.uuid);
                    break;
                case PropertyType.NAME:
                    validators.push(this.validators.name);
                    break;
                case PropertyType.PASSWORD:
                    validators.push(this.validators.password);
                    break;
                default:
                    break;
            }
        }

        let error = validators.map(validator=>validator(newValue, oldProperty)).filter(error => error !== "");
        return error;
    }

    createEditValidation()
    {
        const checkPropertiesForErrors = (properties: (IPropertyState|IObjectsState)[]): boolean => {
            let foundError = false;
            properties.forEach((item: IPropertyState|IObjectsState) => {
                
                if(this.isObjectsState(item))
                {
                    foundError = checkPropertiesForErrors(item.objects[item.selectHandle].properties) || foundError;
                    return;
                }

                let errors = this.checkValidation(item.value, item, true);
                item.errors = errors;
                foundError = (errors && errors.length > 0) || foundError;
            });

            return foundError;
        }
        let properties = _.cloneDeep(this.state.object.properties);
        let foundErrors = checkPropertiesForErrors(properties);
        this.setState({
            object: {
                ...this.state.object,
                properties: properties
            }
        });

        return !foundErrors;
    }

    // ============================================ //
    //              Convert Functions               //
    // ============================================ //

    convertToJson(object: IObjectState)
    {
        const convertProperties = (properties: (IPropertyState|IObjectsState)[])=>{
            return {
                ...properties.reduce((map: any, item) => {
                    map[item.id] = this.isObjectsState(item) ? 
                        {
                            id: item.objects[item.selectHandle].id,
                            ...convertProperties(item.objects[item.selectHandle].properties)
                        } : 
                        item.array ? 
                            item.valueList : 
                            item.value;
                    return map;
                }, {})
            }
        };
        let result = {
            ...object,
            properties: { ...convertProperties(object.properties) }
        } as any;
        delete result.title;
        delete result.icon;
        return result as ObjectResult;
    }

    convertToObjectState(object: IObject)
    {
        function isObjects(objects: IProperty | IObjects): objects is IObjects {
            return (objects as IObjects).objects !== undefined;
        }

        function convertProperties(properties: (IProperty | IObjects)[]): (IPropertyState | IObjectsState)[]
        {
            return properties.map((item: IProperty | IObjects)=>{
                return isObjects(item) ?
                    {
                        ...item,
                        selectHandle: 0,
                        objects: item.objects.map((obj: IObject)=>({
                            ...obj,
                            properties: convertProperties(obj.properties)
                        } as IObjectState))
                    } as IObjectsState : 
                    {
                        ...item,
                        value: item.value ?? "",
                        valueList: [],
                        errors: []
                    } as IPropertyState
            })
        }

        return {
            ...object,
            properties: convertProperties(object.properties)
        } as IObjectState;
    }
}

// ----------------- Export -----------------  //

const mapStateToProps = (state: AppState, props: NavigationTabProps): StateProps => ({
    user: state.user.current,
    services: state.services.myActiveServices
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{},{},any>, props: NavigationTabProps): DispatchProps => ({
    loading: ()=>dispatch(loadingOverlay(true)),
    doneLoading: ()=>dispatch(loadingOverlay(false)),
    broadcast: (msg, type?)=>dispatch(broadcastMessage(msg, type))
});

export default withStyles(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(CreateEditDialog)));



// ------------------------------------------  //